import React, { useState, useEffect } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { Home, Edit } from '@mui/icons-material';
import { Video } from '../../types';
import VideosApiService from '../../services/videosApi';
import VideoDetail from './VideoDetail';
import VideoForm from './VideoForm';
import { useNotify } from '../../contexts/NotificationContext';

const VideoView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const { notify } = useNotify('content');
  const { environment: env } = useEnvironment();

  const loadVideo = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await VideosApiService.getVideoById(env, id);
      setVideo(data);
    } catch (error) {
      notify('Erreur lors du chargement de la vidéo', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideo();
    // loadVideo lit id et env : la vidéo se recharge quand l'un d'eux change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, env]);

  const handleEdit = () => {
    setOpenForm(true);
  };

  const handleFormSubmit = async (data: Omit<Video, 'id'>) => {
    if (!id) return;

    try {
      await VideosApiService.updateVideo(env, id, data);
      notify('Vidéo mise à jour avec succès', 'success');
      setOpenForm(false);
      loadVideo();
    } catch (error) {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!video) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5">Vidéo non trouvée</Typography>
        <Button onClick={() => navigate('/videos')} sx={{ mt: 2 }}>
          Retour aux vidéos
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Accueil
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/videos')}
          sx={{ cursor: 'pointer' }}
        >
          Vidéos
        </Link>
        <Typography color="text.primary">{video.title}</Typography>
      </Breadcrumbs>

      {/* Informations de la vidéo */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h5" component="h1">
            {video.title}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Modifier
          </Button>
        </Box>
        <VideoDetail video={video} />
      </Paper>

      {/* Formulaire de modification */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier la vidéo</DialogTitle>
        <DialogContent>
          <VideoForm
            video={video}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default VideoView;
